import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Link, Navigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {ArrowRight, Copy, DotsThreeVertical, Plugs, Power, Trash} from '../../icons';
import {useDismissOnOutside} from '../../../hooks/useDismissOnOutside';
import {APIClient} from '../../../utils/APIClient';
import {isOk} from '../../../utils/apiResponse';
import {API_URL} from '../../../settings/local_settings';
import {Spinner} from '../../Spinner';

/**
 * El menú `⋮` de una línea de control — las acciones que no son "editar presupuesto y objetivo".
 *
 * **Por qué es un menú y no un atajo al drawer.** Hasta el 2026-09-10 este mismo botón abría el
 * `LineEditDrawer` con el scroll puesto en «Gestión», o sea EL MISMO destino que el lápiz de al
 * lado: la única diferencia era dónde aterrizaba la vista. Pablo lo miró y preguntó «estos tres
 * puntitos, ¿qué son? Es lo mismo que editar línea» (card yg9Z6jW0). Tenía razón, y hay una razón
 * más fuerte que la ambigüedad: **el mismo ícono ya significa "menú" en la pantalla de al lado** —
 * `ClientV2Row` del Listado v2 lo usa para desplegar Desactivar/Eliminar tras un divisor. El
 * usuario aprende el gesto ahí y acá recibía otra cosa.
 *
 * Con esto, desactivar y eliminar quedan a UN click en vez de dos más un scroll.
 *
 * **Un componente para las dos superficies** (la card del Panel y la tabla de `/client/:id/lines`),
 * por la misma razón que `boosterSteps.js`: dos copias de la misma lista terminan con una pantalla
 * que la otra no tiene. Lo que cambia entre superficies es sólo el tamaño del botón, y eso entra
 * por `triggerClassName`.
 *
 * **Desactivar, reactivar y eliminar no escriben acá.** Los tres callbacks avisan al shell, que es
 * quien confirma con `YesNoModal` y llama a `lineManageActions.js`. Un solo camino de escritura, dos
 * puertas (invariante de la card IysOiDWB).
 *
 * **«Duplicar esta línea» sí escribe acá** (card YySIehcb, tanda «Feedback de iVirtual»): no tiene
 * confirmación —la copia no toca la original— ni otra puerta, y lo que sigue no es refrescar la
 * tabla sino abrir la copia para editarla. Mientras el pedido viaja, el ítem queda deshabilitado y
 * el menú abierto: un doble clic no puede crear dos copias.
 *
 * ⚠️ **El panel va `fixed` y posicionado a mano, NO `absolute` bajo el trigger.** Con `absolute` lo
 * recorta cualquier ancestro que sea un scrollport, y acá hay uno real: debajo de 1180px
 * `LinesView.scss` le da `overflow-x: auto` a `.plv-tw`. Medido el 10/09 con esa regla activa, el
 * menú de la ÚLTIMA fila perdía **159 de sus 168 px** — o sea que Desactivar y Eliminar quedaban
 * invisibles justo en la vista donde se gestionan las líneas. Es el mismo mecanismo del gotcha del
 * tooltip anclado a su disparador, y el mismo recurso que usa el submenú flotante del sidebar:
 * medir el trigger con `getBoundingClientRect()` y dibujar contra el viewport.
 */
export function LineActionsMenu({line, onDeactivate, onActivate, onDelete, triggerClassName}) {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    // `null` mientras no se midió: el panel no se dibuja hasta tener coordenadas, así que nunca
    // aparece un frame en la esquina superior izquierda.
    const [pos, setPos] = useState(null);
    // El ref envuelve al TRIGGER Y AL PANEL, como pide `useDismissOnOutside`. Lo que de verdad
    // depende de eso —medido por mutación— es que un click adentro del menú no lo cierre: apuntar
    // el ref a la nada hace caer el guardián de eso, y ningún otro.
    const menuRef = useRef(null);

    const triggerRef = useRef(null);
    const listRef = useRef(null);

    const close = useCallback(() => setIsOpen(false), []);
    useDismissOnOutside(menuRef, isOpen, close);

    /**
     * Dónde dibujar el panel: alineado al borde DERECHO del trigger (estas acciones viven al final
     * de la fila, así que abrir hacia la izquierda es lo único que lo mantiene en pantalla) y
     * debajo, salvo que no haya alto libre — ahí abre hacia arriba.
     *
     * `useLayoutEffect` y no `useEffect`: corre antes del pintado, así que el panel no se ve saltar
     * de una posición a la otra.
     */
    /**
     * Hacia qué lado abre, decidido UNA sola vez por apertura.
     *
     * Recalcularlo en cada scroll hace que el panel salte de arriba a abajo del `⋮` a mitad del
     * gesto (medido: 60 px de scroll lo movían 146). El lado es una decisión de apertura; el
     * desplazamiento, no.
     *
     * ⚠️ La decisión se toma en la primera medición **que tenga alto**, no «mientras no lo tenga».
     * La versión anterior asumía que en la primera pasada `offsetHeight` daba 0 y fijaba el lado
     * ahí; es falso — el panel oculto se dibuja con `visibility: hidden`, no con `display: none`,
     * así que YA mide 168 px en esa pasada. La condición nunca se cumplía, `ladoArriba` se quedaba
     * en el `false` inicial **para siempre** y el menú no abría hacia arriba nunca: en la última
     * fila del Panel se salía 125 px del viewport y «Desactivar» y «Eliminar» quedaban fuera de
     * pantalla (QA-panel-A10 / H3, QA-VIS-panel-01). Era el mismo defecto que este frente vino a
     * arreglar, por el otro eje.
     */
    const ladoArriba = useRef(false);
    const ladoDecidido = useRef(false);

    const medir = useCallback(() => {
        if (!triggerRef.current) return null;
        // `rect` y no `t`: en este archivo `t` es la función de i18n.
        const rect = triggerRef.current.getBoundingClientRect();
        const alto = (listRef.current && listRef.current.offsetHeight) || 0;
        // ⚠️ `documentElement.clientWidth/Height` y NO `window.innerWidth/Height`: el `right` de un
        // `position: fixed` y el `getBoundingClientRect()` se miden contra el borde INTERNO del
        // viewport (sin la scrollbar), y `window.innerWidth` la incluye. Con la scrollbar clásica de
        // Windows eso abría el panel ~15 px a la izquierda del `⋮` en vez de alineado.
        const anchoUtil = document.documentElement.clientWidth;
        const altoUtil = document.documentElement.clientHeight;
        // Con `alto` en 0 la comparación diría siempre «cabe abajo», así que no se decide todavía:
        // se espera a la medición que sí tiene el panel dibujado.
        if (!ladoDecidido.current && alto > 0) {
            ladoArriba.current = rect.bottom + 6 + alto > altoUtil;
            ladoDecidido.current = true;
        }
        const cabeAbajo = !ladoArriba.current;
        return {
            top: cabeAbajo ? rect.bottom + 6 : Math.max(8, rect.top - 6 - alto),
            right: Math.max(8, anchoUtil - rect.right),
            // Si la fila se fue de la pantalla, el menú no tiene a qué acompañar.
            visible: rect.bottom > 0 && rect.top < altoUtil,
        };
    }, []);

    /**
     * Depende de `pos` para poder medir DOS veces por apertura: la primera puede caer con el panel
     * todavía sin dibujar, y recién la segunda tiene el alto con el que se decide el lado.
     *
     * ⚠️ Lo que corta la recursión es que la MEDIDA no cambie, no `ladoDecidido`: en jsdom
     * `offsetHeight` es siempre 0, así que el lado nunca se decidiría y el efecto se llamaría a sí
     * mismo hasta el «Maximum update depth exceeded». Comparar el resultado sirve en los dos
     * entornos.
     */
    useLayoutEffect(() => {
        if (!isOpen) return;
        const medida = medir();
        if (!medida) return;
        if (pos && pos.top === medida.top && pos.right === medida.right) return;
        setPos(medida);
    }, [isOpen, pos, medir]);

    /**
     * Anclado al viewport, el panel no acompaña solo a su fila: si la página se mueve quedaría
     * flotando sobre otra línea, con las acciones de la equivocada.
     *
     * **Acompaña en vez de cerrarse**, y sólo cierra cuando el trigger se fue de la pantalla. Antes
     * cerraba con CUALQUIER scroll, y como el listener va con `capture: true` agarra también el de
     * cualquier descendiente y el inercial del trackpad, que sigue emitiendo eventos después de que
     * el dedo se levantó: apuntarle a «Eliminar» detrás de un scroll suave lo cerraba en la cara.
     */
    useEffect(() => {
        if (!isOpen) return undefined;
        const seguir = () => {
            const medida = medir();
            if (!medida || !medida.visible) {
                setIsOpen(false);
                return;
            }
            setPos(medida);
        };
        window.addEventListener('scroll', seguir, true);
        window.addEventListener('resize', seguir);
        return () => {
            window.removeEventListener('scroll', seguir, true);
            window.removeEventListener('resize', seguir);
        };
    }, [isOpen, medir]);

    const isInactive = line.status === 'INACTIVE';

    /**
     * El foco entra al menú al abrir y VUELVE al trigger al cerrar.
     *
     * Sin lo segundo, cerrar con Escape desmonta el ítem enfocado y el foco cae a `<body>`: quien
     * navega con teclado pierde el lugar y tiene que tabular la fila entera de nuevo. `devolverFoco`
     * es un ref y no un estado porque sólo importa en el instante del cierre.
     */
    const devolverFoco = useRef(false);

    useEffect(() => {
        if (isOpen) {
            devolverFoco.current = true;
            // ⚠️ Depende de `pos`, no sólo de `isOpen`: hasta que la medición no vuelve, la lista se
            // dibuja con `visibility: hidden` (para no mostrarse un frame en la esquina), y un
            // elemento oculto NO ES FOCUSABLE — el `.focus()` no hacía nada y el foco se quedaba en
            // el trigger. No se ve en el diff; se vio abriendo el menú y preguntando por
            // `document.activeElement`.
            if (!pos) return undefined;
            // Sin `requestAnimationFrame`: cuando este efecto corre, React ya aplicó el `style` con
            // la posición y el panel es visible, así que el `focus()` prende. Con el rAF de por
            // medio el foco se quedaba en el trigger —verificado en pantalla— porque el cleanup del
            // re-render siguiente cancelaba el frame antes de que llegara a correr.
            const primero = listRef.current && listRef.current.querySelector('.pnl-menu__item');
            if (primero) primero.focus();
            return undefined;
        }
        if (devolverFoco.current) {
            devolverFoco.current = false;
            if (triggerRef.current) triggerRef.current.focus();
        }
        return undefined;
    }, [isOpen, pos]);

    // Flechas para recorrer, Home/End para los extremos. Es un `role="menu"`: el patrón ARIA pide
    // que las flechas muevan el foco, no el Tab.
    const handleMenuKeyDown = (event) => {
        const teclas = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
        if (!teclas.includes(event.key)) return;
        const items = [...listRef.current.querySelectorAll('.pnl-menu__item')];
        if (!items.length) return;
        event.preventDefault();
        const actual = items.indexOf(document.activeElement);
        let siguiente;
        if (event.key === 'Home') siguiente = 0;
        else if (event.key === 'End') siguiente = items.length - 1;
        else if (event.key === 'ArrowDown') siguiente = (actual + 1) % items.length;
        else siguiente = (actual <= 0 ? items.length : actual) - 1;
        items[siguiente].focus();
    };

    // Elegir un ítem cierra el menú SIEMPRE, también cuando lo que sigue es un modal de
    // confirmación: dejarlo abierto detrás del modal deja dos superficies apiladas hablando de la
    // misma línea.
    const run = (action) => () => {
        // El modal que sigue se lleva el foco: devolverlo al trigger acá se lo robaría.
        devolverFoco.current = false;
        close();
        if (action) action(line);
    };

    /**
     * Duplicar: un POST y, si sale bien, a la edición completa de la copia (D10 de la tanda).
     *
     * El candado es un ref Y un estado: el estado deshabilita el ítem en pantalla, pero dos clics en
     * el mismo tick llegan antes de que React vuelva a dibujar, y sólo el ref los ve.
     *
     * Se navega con `<Navigate>` y no con `useNavigate()`: el menú lo montan suites que mockean el
     * router con `Link` solo (las de `LinesView` abren este menú), y un hook en cada render las
     * rompería por algo que sólo pasa cuando la copia ya existe.
     */
    const duplicando = useRef(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [copyPath, setCopyPath] = useState(null);

    const duplicate = () => {
        if (duplicando.current) return;
        duplicando.current = true;
        setIsDuplicating(true);
        const fallar = () => {
            toast.error(t('client_panel.line_duplicate_error', 'No pudimos duplicar la línea. Intenta de nuevo.'));
            duplicando.current = false;
            setIsDuplicating(false);
        };
        new APIClient(`${API_URL}business-unit/${encodeURIComponent(line.id)}/duplicate/`).call('POST', {})
            .then((response) => {
                const copyId = isOk(response) && response.data ? response.data.business_unit_id : null;
                if (!copyId) {
                    fallar();
                    return;
                }
                toast.success(t('client_panel.line_duplicate_done',
                    'Línea duplicada. Cambia el nombre y las campañas de la copia.'));
                devolverFoco.current = false;
                close();
                setCopyPath(`/business-unit/${copyId}/`);
            })
            .catch(fallar);
    };

    return (
        <span className="pnl-menu" ref={menuRef}>
            <button
                type="button"
                ref={triggerRef}
                className={triggerClassName}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                title={t('client_panel.line_menu_aria', 'Más acciones de esta línea')}
                aria-label={t('client_panel.line_menu_aria', 'Más acciones de esta línea')}
                onClick={() => {
                    setPos(null);
                    ladoDecidido.current = false;   // cada apertura decide su lado de nuevo
                    setIsOpen((open) => !open);
                }}
            >
                <DotsThreeVertical size={16} weight="bold"/>
            </button>

            {isOpen && (
                <span className="pnl-menu__list" role="menu" ref={listRef}
                      onKeyDown={handleMenuKeyDown}
                      style={pos ? {top: pos.top, right: pos.right} : {visibility: 'hidden'}}>
                    {/* Las fuentes (qué cuentas y campañas mide la línea) son lo que más se toca
                        después del presupuesto y el objetivo. Mismo destino y misma clave que el
                        link del pie del drawer: se nombra igual en los dos lados o son dos cosas. */}
                    <Link className="pnl-menu__item" role="menuitem"
                          to={`/business-unit/${line.id}/accounts`} onClick={close}>
                        <Plugs size={15}/>
                        {t('client_panel.line_drawer_sources', 'Fuentes de datos')}
                    </Link>
                    {/* El reemplazo del «Editar línea» que vivía dentro del chevron: ahí era una
                        acción escondida en un acordeón de LECTURA, y su rótulo no decía que te
                        sacaba del Panel. Acá el destino está nombrado. */}
                    <Link className="pnl-menu__item" role="menuitem"
                          to={`/business-unit/${line.id}/`} onClick={close}>
                        <ArrowRight size={15}/>
                        {t('client_panel.line_menu_open_full', 'Abrir la línea completa')}
                    </Link>

                    <span className="pnl-menu__divider" role="separator"/>

                    {/* Arriba de Desactivar: es la acción de gestión que no destruye nada. Sobre una
                        línea inactiva también, porque la copia nace activa. */}
                    <button type="button" className="pnl-menu__item" role="menuitem"
                            disabled={isDuplicating} aria-busy={isDuplicating}
                            onClick={duplicate}>
                        {/* El spinner chico mide 12px y el ícono 15: la caja de 15 evita que el texto
                            se corra 3px mientras espera (medido en Chrome, 09/10). */}
                        {isDuplicating
                            ? <span style={{display: 'inline-flex', justifyContent: 'center', width: 15}}>
                                <Spinner color="positive very-small"/>
                              </span>
                            : <Copy size={15}/>}
                        {t('client_panel.line_menu_duplicate', 'Duplicar esta línea')}
                    </button>

                    {/* Desactivar y eliminar van JUNTOS, con la línea activa o inactiva. El clásico
                        (`StatusToggl`) y el Listado v2 esconden el tacho hasta que la fila está
                        desactivada; acá no, y es deliberado desde antes de esta card: la 2.1 es la
                        superficie más permisiva de las tres y no se regresiona. */}
                    {/* Cada ítem se dibuja sólo si hay quien lo atienda. El comentario del shell
                        dice «van los tres o ninguno», pero el código no lo obligaba: un
                        `onDeactivate` sin cablear renderizaba un ítem que sólo cerraba el menú —
                        una acción que existe en pantalla y no hace nada. */}
                    {isInactive ? (onActivate && (
                        <button type="button" className="pnl-menu__item" role="menuitem"
                                onClick={run(onActivate)}>
                            <Power size={15}/>
                            {t('client_panel.line_manage_activate', 'Reactivar esta línea')}
                        </button>
                    )) : (onDeactivate && (
                        <button type="button" className="pnl-menu__item" role="menuitem"
                                onClick={run(onDeactivate)}>
                            <Power size={15}/>
                            {t('client_panel.line_manage_deactivate', 'Desactivar esta línea')}
                        </button>
                    ))}
                    {onDelete && (
                        <button type="button" className="pnl-menu__item -danger" role="menuitem"
                                onClick={run(onDelete)}>
                            <Trash size={15}/>
                            {t('client_panel.line_menu_delete', 'Eliminar…')}
                        </button>
                    )}
                </span>
            )}

            {copyPath && <Navigate to={copyPath}/>}
        </span>
    );
}

export default LineActionsMenu;
